import React, { useState } from 'react'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableBody from '@mui/material/TableBody'
import IconButton from '@mui/material/IconButton'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import DoneIcon from '@mui/icons-material/Done'
import CloseIcon from '@mui/icons-material/Close'
import { useTranslation } from 'react-i18next'
import CardContentText from 'Components/reusable/CaleoCustomComponents/CardContentText'
import { InputField } from 'Components/reusable/InputFields/InputField'
import { IParsedCombinedCompetence, IParsedCompetenceTranslation } from 'types/cvParserInterfaces'
import { convertToDisplayDate } from 'utils/utils'
import { hasValidationErrors, validateCompetence } from 'utils/editDataValidation'

/** @notExported */
interface IParsedCompetencesTableProps {
  /** Parsed competences */
  competences: IParsedCombinedCompetence[]
  /** Called with the edited competences */
  onChange: (competences: IParsedCombinedCompetence[]) => void
  /** Language the names are shown and edited in */
  languageId: number
  /** Show without actions (the CV is already saved) */
  readOnly?: boolean
  /** Shown after a competence's name, e.g. whether saving would create a new competence for it */
  renderStatus?: (competence: IParsedCombinedCompetence) => React.ReactNode
}

/**
 * Parsed competences: name and issuer are edited per language, dates are shown at the CV's precision
 *
 * @returns The table
 * @notExported
 */
const ParsedCompetencesTable: React.FC<IParsedCompetencesTableProps> = ({
  competences,
  onChange,
  languageId,
  readOnly = false,
  renderStatus,
}) => {
  const { t } = useTranslation()
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [temp, setTemp] = useState<IParsedCombinedCompetence | null>(null)

  const translationOf = (competence: IParsedCombinedCompetence) =>
    competence.translations.find(tr => tr.languageId === languageId)
  const setField = (field: 'name' | 'issuer', value: string) => {
    if (!temp) return
    const translation: IParsedCompetenceTranslation = {
      name: '',
      issuer: '',
      ...translationOf(temp),
      languageId,
      [field]: value,
    }
    setTemp({ ...temp, translations: [...temp.translations.filter(tr => tr.languageId !== languageId), translation] })
  }
  const errors = temp ? validateCompetence(temp, languageId) : {}

  return (
    <>
      <CardContentText type="darkSubHeader">{t('admin.parse.result.competences')}</CardContentText>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t('admin.parse.result.name')}</TableCell>
            <TableCell>{t('admin.parse.result.issuer')}</TableCell>
            <TableCell>{t('admin.parse.result.issueDate')}</TableCell>
            <TableCell>{t('admin.parse.result.endDate')}</TableCell>
            {!readOnly && <TableCell width={100}>{t('admin.parse.result.actions')}</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {competences.map((competence, index) => {
            const isEditing = editingIndex === index && temp !== null
            const translation = translationOf(isEditing ? temp : competence)
            return (
              <TableRow key={index}>
                <TableCell>
                  {isEditing ? (
                    <InputField
                      value={translation?.name ?? ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('name', e.target.value)}
                      error={errors.name}
                      fullWidth
                      data-testid={`field-admin-parse-competence-name-${index}`}
                    />
                  ) : (
                    <>
                      {translation?.name}
                      {renderStatus?.(competence)}
                    </>
                  )}
                </TableCell>
                <TableCell>
                  {isEditing ? (
                    <InputField
                      value={translation?.issuer ?? ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('issuer', e.target.value)}
                      error={errors.issuer}
                      fullWidth
                      data-testid={`field-admin-parse-competence-issuer-${index}`}
                    />
                  ) : (
                    translation?.issuer
                  )}
                </TableCell>
                <TableCell>{convertToDisplayDate(competence.issueDate, competence.dateResolution)}</TableCell>
                <TableCell>{convertToDisplayDate(competence.endDate, competence.dateResolution)}</TableCell>
                {!readOnly && (
                  <TableCell>
                    {isEditing ? (
                      <>
                        <IconButton
                          data-testid={`button-admin-parse-competence-save-${index}`}
                          size="small"
                          color="primary"
                          disabled={hasValidationErrors(errors)}
                          onClick={() => {
                            onChange(competences.map((other, i) => (i === index ? temp : other)))
                            setEditingIndex(null)
                          }}
                        >
                          <DoneIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          data-testid={`button-admin-parse-competence-cancel-${index}`}
                          size="small"
                          onClick={() => setEditingIndex(null)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <IconButton
                          data-testid={`button-admin-parse-competence-edit-${index}`}
                          size="small"
                          onClick={() => {
                            setEditingIndex(index)
                            setTemp(competence)
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          data-testid={`button-admin-parse-competence-delete-${index}`}
                          size="small"
                          onClick={() => onChange(competences.filter((_, i) => i !== index))}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                )}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </>
  )
}

export default ParsedCompetencesTable
